import { Fragment, type FocusEvent, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button.tsx';
import { ArrowLeft } from 'lucide-react';
import { useNotify } from 'react-admin';
import { Input } from '../ui/input.tsx';
import type { Specification } from './SpecDataList.tsx';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

interface SpecDataDetailProps {
  spec: Specification;
  onBack: () => void;
  onUpdated: (spec: Specification) => void;
}

type EditableField = 'parameter' | 'testMethod' | 'uom' | 'specMin' | 'specMax';

export default function SpecDataDetail({ spec, onBack, onUpdated }: SpecDataDetailProps) {
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5180/api';
  const notify = useNotify();
  const [draftSpec, setDraftSpec] = useState<Specification>(spec);
  const [editingItemKey, setEditingItemKey] = useState<string | null>(null);
  const [savingItemKey, setSavingItemKey] = useState<string | null>(null);
  const draftSpecRef = useRef(draftSpec);

  useEffect(() => {
    draftSpecRef.current = draftSpec;
  }, [draftSpec]);

  useEffect(() => {
    setDraftSpec(spec);
    draftSpecRef.current = spec;
    setEditingItemKey(null);
    setSavingItemKey(null);
  }, [spec]);

  const formatDateOnly = (value: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    if (date.getUTCFullYear() === 1970 && date.getUTCMonth() === 0 && date.getUTCDate() === 1) return '-';
    return date.toISOString().slice(0, 10);
  };

  const formatCellValue = (value: number | string | null | undefined) => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };

  const groupedItems = draftSpec.items.reduce<Record<string, Array<{ item: Specification['items'][number]; index: number }>>>((groups, item, index) => {
    const key = item.classification || 'General';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push({ item, index });
    return groups;
  }, {});

  const groupedEntries = Object.entries(groupedItems);
  const summaryFields = [
    { label: 'Batch Number', value: draftSpec.batchNumber || '-' },
    { label: 'Product Description', value: draftSpec.productDescription || '-' },
    { label: 'Manufacturer', value: draftSpec.manufacturer || '-' },
    { label: 'Spec Number', value: draftSpec.specNumber || '-' },
    { label: 'Sample Submission Ref Number', value: draftSpec.sampleSubmissionRefNumber || '-' },
    { label: 'Manufacture Date', value: formatDateOnly(draftSpec.manufactureDate) },
    { label: 'Expiry Date', value: formatDateOnly(draftSpec.expiryDate) },
  ];

  const updateItemField = (index: number, field: EditableField, value: string) => {
    setDraftSpec((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => {
        if (itemIndex !== index) return item;
        if (field === 'specMin' || field === 'specMax') {
          return { ...item, [field]: value.trim() === '' ? null : value };
        }
        return { ...item, [field]: value };
      }),
    }));
  };

  const buildPayload = (specValue: Specification) => ({
    BatchNumber: specValue.batchNumber,
    ProductDescription: specValue.productDescription,
    Manufacturer: specValue.manufacturer,
    ProductType: specValue.productType,
    CountryOfOrigin: specValue.countryOfOrigin,
    SpecNumber: specValue.specNumber,
    ReportNumber: specValue.reportNumber,
    SampleSubmissionRefNumber: specValue.sampleSubmissionRefNumber,
    SampleReceivedDate: specValue.sampleReceivedDate || null,
    Packaging: specValue.packaging,
    TestingPurpose: specValue.testingPurpose,
    Client: specValue.client,
    ManufactureDate: specValue.manufactureDate || null,
    ExpiryDate: specValue.expiryDate || null,
    Items: specValue.items.map((item) => ({
      Parameter: item.parameter,
      Classification: item.classification,
      TestMethod: item.testMethod,
      Uom: item.uom,
      SpecMin: item.specMin ?? null,
      SpecMax: item.specMax ?? null,
    })),
  });

  const saveSpec = async (rowKey: string) => {
    if (savingItemKey) return;
    const nextSpec = draftSpecRef.current;
    setSavingItemKey(rowKey);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/specifications/${nextSpec.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(buildPayload(nextSpec)),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const updated = await res.json().catch(() => null);
      const normalized: Specification = {
        ...nextSpec,
        ...(updated ?? {}),
        id: String(updated?.id ?? nextSpec.id),
      };

      draftSpecRef.current = normalized;
      setDraftSpec(normalized);
      onUpdated(normalized);
      notify('Specification saved successfully', { type: 'success' });
    } catch (error: any) {
      console.error('Save specification item error', error);
      setEditingItemKey(rowKey);
      notify(`Save failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setSavingItemKey(null);
    }
  };

  const handleRowBlur = (rowKey: string) => (event: FocusEvent<HTMLTableRowElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) return;
    if (editingItemKey !== rowKey) return;

    setEditingItemKey(null);
    window.setTimeout(() => {
      void saveSpec(rowKey);
    }, 0);
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 border-b bg-gradient-to-r from-slate-50 to-white px-5 py-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-semibold tracking-tight">Specification Detail</h2>
        </div>
        <Button onClick={onBack} variant="outline" className="flex w-fit items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
      </div>

      <div className="border-b border-slate-100 px-5 py-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          {summaryFields.map((field) => (
            <div key={field.label} className="flex min-w-0 items-center gap-2 text-sm">
              <span className="shrink-0 font-medium text-slate-500">{field.label}:</span>
              <span className="min-w-0 break-words text-slate-900">{field.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div>
        {groupedEntries.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No specification items found.</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
              Double-click a row to edit. Click outside the row to save.
            </div>
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[34%]" />
                <col className="w-[20%]" />
                <col className="w-[12%]" />
                <col className="w-[17%]" />
                <col className="w-[17%]" />
              </colgroup>
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Parameter</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Test Method</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">UOM</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Spec Min</th>
                  <th className="whitespace-nowrap px-4 py-3 text-left font-medium">Spec Max</th>
                </tr>
              </thead>
              <tbody>
                {groupedEntries.map(([classification, items]) => (
                  <Fragment key={`group-${classification}`}>
                    <tr key={`group-${classification}`} className="border-t-2 border-slate-200 bg-slate-700/90 text-slate-100">
                      <td colSpan={5} className="px-4 py-2">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs font-semibold uppercase tracking-[0.14em]">{classification}</span>
                          <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium text-slate-100">
                            {items.length} item{items.length === 1 ? '' : 's'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {items.map(({ item, index }) => {
                      const rowKey = String(item.id ?? `${classification}-${index}`);
                      const isEditing = editingItemKey === rowKey;
                      const isSaving = savingItemKey === rowKey;

                      return (
                        <tr
                          key={rowKey}
                          className={`border-t border-slate-100 ${isEditing ? 'bg-slate-50/90' : 'hover:bg-slate-50/70'} ${isSaving ? 'opacity-70' : ''}`}
                          onDoubleClick={() => setEditingItemKey(rowKey)}
                          onBlur={handleRowBlur(rowKey)}
                        >
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                autoFocus
                                value={draftSpec.items[index]?.parameter ?? ''}
                                onChange={(event) => updateItemField(index, 'parameter', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="whitespace-pre-line break-words">{item.parameter || '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                value={draftSpec.items[index]?.testMethod ?? ''}
                                onChange={(event) => updateItemField(index, 'testMethod', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="whitespace-pre-line break-words">{item.testMethod || '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                value={draftSpec.items[index]?.uom ?? ''}
                                onChange={(event) => updateItemField(index, 'uom', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="break-words">{item.uom || '-'}</span>
                            )}
                          </td>
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                value={draftSpec.items[index]?.specMin ?? ''}
                                onChange={(event) => updateItemField(index, 'specMin', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="break-words" title={formatCellValue(item.specMin)}>{formatCellValue(item.specMin)}</span>
                            )}
                          </td>
                          <td className="px-4 py-1.5 align-top break-words">
                            {isEditing ? (
                              <Input
                                value={draftSpec.items[index]?.specMax ?? ''}
                                onChange={(event) => updateItemField(index, 'specMax', event.target.value)}
                                disabled={isSaving}
                                className="h-9 bg-white"
                              />
                            ) : (
                              <span className="break-words" title={formatCellValue(item.specMax)}>{formatCellValue(item.specMax)}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
